/**
 * Vulnerability Management Context
 *
 * State management for projects, scans, and overview metrics.
 */
import { createContext, useState, useContext, useCallback } from 'react';
import VulnerabilityManagementService from '../services/VulnerabilityManagementService';

const VulnerabilityManagementContext = createContext();

export const useVulnerabilityManagement = () => {
  const context = useContext(VulnerabilityManagementContext);
  if (!context) {
    throw new Error('useVulnerabilityManagement must be used within VulnerabilityManagementProvider');
  }
  return context;
};

export const VulnerabilityManagementProvider = ({ children }) => {
  // ===== State =====

  const [overview, setOverview] = useState({
    totalProjects: 0,
    totalScans: 0,
    vulnerabilities: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    unassigned: 0,
    components: 0
  });

  const [projects, setProjects] = useState([]);
  const [projectsTotal, setProjectsTotal] = useState(0);

  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [error, setError] = useState(null);

  // ===== Data Fetching =====

  const fetchOverview = useCallback(async () => {
    setLoadingOverview(true);
    setError(null);
    try {
      const data = await VulnerabilityManagementService.getOverview();
      setOverview(data);
    } catch (err) {
      console.error('Error fetching overview:', err);
      setError(err.detail || err.message || 'Failed to fetch overview');
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const fetchProjects = useCallback(async (pageNumber = 1, pageSize = 10) => {
    setLoadingProjects(true);
    setError(null);
    try {
      const data = await VulnerabilityManagementService.listProjects(pageNumber, pageSize);
      setProjects(data?.projects || []);
      setProjectsTotal(data?.total || 0);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.detail || err.message || 'Failed to fetch projects');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // ===== Mutations =====

  const createProject = useCallback(async (projectName) => {
    setError(null);
    try {
      await VulnerabilityManagementService.createProject(projectName);
      await Promise.all([fetchOverview(), fetchProjects()]);
    } catch (err) {
      console.error('Error creating project:', err);
      const msg = err.detail || err.message || 'Failed to create project';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchOverview, fetchProjects]);

  const deleteProject = useCallback(async (uuid) => {
    setError(null);
    try {
      await VulnerabilityManagementService.deleteProject(uuid);
      await Promise.all([fetchOverview(), fetchProjects()]);
    } catch (err) {
      console.error('Error deleting project:', err);
      const msg = err.detail || err.message || 'Failed to delete project';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchOverview, fetchProjects]);

  const uploadScan = useCallback(async (projectUuid, componentName, version, sbomFile, isLatest = true) => {
    setError(null);
    try {
      const data = await VulnerabilityManagementService.uploadScan(
        projectUuid, componentName, version, sbomFile, isLatest
      );
      await Promise.all([fetchOverview(), fetchProjects()]);
      return data;
    } catch (err) {
      console.error('Error uploading scan:', err);
      const msg = err.detail || err.message || 'Failed to upload scan';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchOverview, fetchProjects]);

  const deleteScan = useCallback(async (uuid) => {
    setError(null);
    try {
      await VulnerabilityManagementService.deleteScan(uuid);
      await Promise.all([fetchOverview(), fetchProjects()]);
    } catch (err) {
      console.error('Error deleting scan:', err);
      const msg = err.detail || err.message || 'Failed to delete scan';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchOverview, fetchProjects]);

  const setScanTracked = useCallback(async (uuid, isLatest) => {
    setError(null);
    try {
      await VulnerabilityManagementService.setScanTracked(uuid, isLatest);
      await Promise.all([fetchOverview(), fetchProjects()]);
    } catch (err) {
      console.error('Error setting scan tracking:', err);
      const msg = err.detail || err.message || 'Failed to update scan tracking';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchOverview, fetchProjects]);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchOverview(), fetchProjects()]);
  }, [fetchOverview, fetchProjects]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ===== Context Value =====

  const value = {
    overview,
    projects,
    projectsTotal,
    loadingOverview,
    loadingProjects,
    error,

    fetchOverview,
    fetchProjects,
    createProject,
    deleteProject,
    uploadScan,
    deleteScan,
    setScanTracked,
    refreshData,
    clearError
  };

  return (
    <VulnerabilityManagementContext.Provider value={value}>
      {children}
    </VulnerabilityManagementContext.Provider>
  );
};

export default VulnerabilityManagementContext;
